/**
 * Application Router
 *
 * Defines all routes and navigation structure
 * Feature: 012-minimal-modern-ui - Updated routing for unified home
 */

import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ROUTES } from '@/lib/utils/constants';

// Lazy load pages for code splitting
import { lazy, Suspense } from 'react';
import { FullPageSpinner } from '@/components/common/LoadingSpinner';

/**
 * Wrapper for lazy imports that handles chunk loading errors
 * When a new deployment invalidates old chunks, this triggers a page reload
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const reloadKey = 'chunk-reload-attempted';
    try {
      const component = await importFn();
      // Clear reload flag on successful import
      sessionStorage.removeItem(reloadKey);
      return component;
    } catch (error) {
      // Check if it's a chunk loading error
      const isChunkError = 
        error instanceof Error && 
        (error.message.includes('Failed to fetch dynamically imported module') ||
         error.message.includes('Loading chunk') ||
         error.message.includes('Loading CSS chunk'));
      
      if (isChunkError && !sessionStorage.getItem(reloadKey)) {
        console.warn('Chunk loading failed, reloading page...', error);
        sessionStorage.setItem(reloadKey, 'true');
        window.location.reload();
        // Return a never-resolving promise while reload happens
        return new Promise(() => {});
      }
      
      // If already tried reloading or not a chunk error, rethrow
      sessionStorage.removeItem(reloadKey);
      throw error;
    }
  });
}

// Pages
const LoginPage = lazyWithRetry(() =>
  import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const AuthCallback = lazyWithRetry(() =>
  import('@/pages/AuthCallback').then((m) => ({ default: m.AuthCallback }))
);
const OnboardingPage = lazyWithRetry(() =>
  import('@/pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage }))
);
const UsernameSetupPage = lazyWithRetry(() =>
  import('@/pages/UsernameSetupPage').then((m) => ({ default: m.UsernameSetupPage }))
);
const UserSearchPage = lazyWithRetry(() =>
  import('@/pages/UserSearchPage').then((m) => ({ default: m.UserSearchPage }))
);
const ProfileEditPage = lazyWithRetry(() =>
  import('@/pages/ProfileEditPage').then((m) => ({ default: m.ProfileEditPage }))
);
// Channel pages (004-channel-chat)
const ChannelRoomPage = lazyWithRetry(() =>
  import('@/pages/ChannelRoomPage').then((m) => ({ default: m.ChannelRoomPage }))
);
const ChannelInvitePage = lazyWithRetry(() =>
  import('@/pages/ChannelInvitePage').then((m) => ({ default: m.ChannelInvitePage }))
);

// DM pages (005-direct-message)
const DMRoomPage = lazyWithRetry(() =>
  import('@/pages/DMRoomPage').then((m) => ({ default: m.DMRoomPage }))
);

// Friend pages (006-friend-system)
const FriendListPage = lazyWithRetry(() =>
  import('@/pages/FriendListPage').then((m) => ({ default: m.FriendListPage }))
);
const FriendRequestPage = lazyWithRetry(() =>
  import('@/pages/FriendRequestPage').then((m) => ({ default: m.FriendRequestPage }))
);
const BlockedUsersPage = lazyWithRetry(() =>
  import('@/pages/BlockedUsersPage').then((m) => ({ default: m.BlockedUsersPage }))
);

// Settings page (012-minimal-modern-ui)
const SettingsPage = lazyWithRetry(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);

// Home page (012-minimal-modern-ui)
const HomePage = lazyWithRetry(() =>
  import('@/components/home/HomePage').then((m) => ({ default: m.HomePage }))
);

// Layout
const AppLayout = lazyWithRetry(() =>
  import('@/components/layout/AppLayout').then((m) => ({ default: m.AppLayout }))
);

// Protected Route
const ProtectedRoute = lazyWithRetry(() =>
  import('@/components/auth/ProtectedRoute').then((m) => ({ default: m.ProtectedRoute }))
);

/**
 * Suspense wrapper for lazy-loaded components
 */
function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<FullPageSpinner />}>{children}</Suspense>;
}

/**
 * Error element for handling chunk loading errors at router level
 * Automatically reloads the page when chunk loading fails after deployment
 */
function ChunkLoadErrorElement() {
  const reloadKey = 'router-chunk-reload';
  
  // Check if it's a chunk loading error and auto-reload
  if (!sessionStorage.getItem(reloadKey)) {
    sessionStorage.setItem(reloadKey, Date.now().toString());
    // Reload after a brief delay to show loading state
    setTimeout(() => window.location.reload(), 100);
    return <FullPageSpinner />;
  }
  
  // If we've already tried reloading, show manual refresh option
  const lastReload = parseInt(sessionStorage.getItem(reloadKey) || '0', 10);
  if (Date.now() - lastReload > 10000) {
    // Clear flag if it's been more than 10 seconds
    sessionStorage.removeItem(reloadKey);
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-primary">
      <div className="text-center p-8">
        <span className="material-icons-round text-4xl text-indigo-400 mb-4 block">refresh</span>
        <h2 className="text-lg font-semibold text-white mb-2">New version available</h2>
        <p className="text-gray-400 mb-4">Please refresh to load the latest version.</p>
        <button
          onClick={() => {
            sessionStorage.removeItem(reloadKey);
            window.location.reload();
          }}
          className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-400 transition-colors"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}

/**
 * Application routes configuration
 * Feature: 012-minimal-modern-ui - Unified home with redirects
 */
export const router = createBrowserRouter([
  // Public routes
  {
    path: ROUTES.LOGIN,
    element: (
      <SuspenseWrapper>
        <LoginPage />
      </SuspenseWrapper>
    ),
    errorElement: <ChunkLoadErrorElement />,
  },
  {
    path: ROUTES.AUTH_CALLBACK,
    element: (
      <SuspenseWrapper>
        <AuthCallback />
      </SuspenseWrapper>
    ),
    errorElement: <ChunkLoadErrorElement />,
  },

  // Channel invite page (004-channel-chat)
  {
    path: '/channel/invite/:code',
    element: (
      <SuspenseWrapper>
        <ProtectedRoute skipOnboardingCheck>
          <ChannelInvitePage />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    errorElement: <ChunkLoadErrorElement />,
  },

  // Onboarding routes - only accessible to users who need setup
  {
    path: ROUTES.ONBOARDING,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute isOnboardingPage>
          <OnboardingPage />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    errorElement: <ChunkLoadErrorElement />,
  },

  // Username setup (after onboarding, before main app)
  {
    path: ROUTES.USERNAME_SETUP,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute isOnboardingPage>
          <UsernameSetupPage />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    errorElement: <ChunkLoadErrorElement />,
  },

  // Main app (protected with layout)
  {
    path: ROUTES.HOME,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    errorElement: <ChunkLoadErrorElement />,
    children: [
      // Default route redirects to friends list (primary landing page)
      {
        index: true,
        element: <Navigate to="/friends" replace />,
      },
      
      // Profile route
      {
        path: 'profile',
        element: <ProfileEditPage />,
      },
      
      // DM room route
      {
        path: 'dm/:dmId',
        element: <DMRoomPage />,
      },
      {
        path: 'channel/:id',
        element: <ChannelRoomPage />,
      },
      
      // Settings page
      {
        path: 'settings',
        element: <SettingsPage />,
      },
      
      // Friend requests & blocked users
      {
        path: 'requests',
        element: <FriendRequestPage />,
      },
      {
        path: 'blocked',
        element: <BlockedUsersPage />,
      },
      
      // Legacy /more routes - redirect to new locations
      {
        path: 'more',
        element: <Navigate to="/settings" replace />,
      },
      {
        path: 'more/profile',
        element: <Navigate to="/profile" replace />,
      },
      {
        path: 'more/requests',
        element: <Navigate to="/requests" replace />,
      },
      {
        path: 'more/blocked',
        element: <Navigate to="/blocked" replace />,
      },
      
      // Search route
      {
        path: 'search',
        element: <UserSearchPage />,
      },

      // ====================================
      // REDIRECTS: Old list pages → Unified Home
      // Feature: 012-minimal-modern-ui
      // ====================================
      
      // Chat list redirect
      {
        path: 'chats',
        element: <Navigate to="/" replace />,
      },
      {
        path: 'chat',
        element: <Navigate to="/" replace />,
      },
      
      // DM list redirect
      {
        path: 'dm',
        element: <Navigate to="/" replace />,
      },
      {
        path: 'dms',
        element: <Navigate to="/" replace />,
      },
      
      // Channel list redirect
      {
        path: 'channels',
        element: <Navigate to="/" replace />,
      },
      
      // Friends list page (012-minimal-modern-ui)
      {
        path: 'friends',
        element: <FriendListPage />,
      },
      
      // Messages page (unified conversation list)
      {
        path: 'messages',
        element: <HomePage />,
      },
      {
        path: 'friends/requests',
        element: <FriendRequestPage />,
      },
      
      // Conversations redirect (legacy 008-modern-simple-ui)
      {
        path: 'conversations',
        element: <Navigate to="/" replace />,
      },
      {
        path: 'conversations/:id',
        element: <Navigate to="/" replace />,
      },
      
      // Legacy settings redirects
      {
        path: 'settings/profile',
        element: <Navigate to="/profile" replace />,
      },
      {
        path: 'settings/blocked',
        element: <Navigate to="/blocked" replace />,
      },
    ],
  },

  // Catch-all redirect to home
  {
    path: '*',
    element: <Navigate to={ROUTES.HOME} replace />,
    errorElement: <ChunkLoadErrorElement />,
  },
]);
